import { useCallback, useContext, useState } from 'react';
import CodeMirror, { EditorView } from '@uiw/react-codemirror';
import {
  history,
  historyKeymap,
  redoDepth,
  undoDepth,
} from '@codemirror/commands';
import { keymap } from '@codemirror/view';
import { cleanContent, getHumanMessage, getLanguageExtension } from 'src/utils';
import {
  useCodeEditorSelection,
  useCodeEditorActions,
  useEditorState,
  useRunEditorStream,
  useCodeAutoSave,
} from 'src/hooks';
import { CodeFloatingElementPlugin } from '../../plugins/CodeFloatingElementPlugin';
import ForwardRefContext from 'src/contexts/ForwardRefContext';
import styles from './CodeEditorContainer.module.scss';
import classNames from 'classnames';
import { EditorGraphInput } from 'src/types';
import { ProgrammingLanguageOptions } from 'src/types';
import { useDebouncedCallback } from 'use-debounce';

const DEBOUNCE_DELAY = 5000;

export const CodeEditorContainer = () => {
  const [view, setView] = useState<EditorView | null>(null);

  const {
    codeEditorData,
    isWordWrapEnabled,
    isEditorDarkTheme,
    isEditorStreaming,
  } = useEditorState();

  const { setCodeEditorData } = useCodeEditorActions();
  const { onCodeAutoSave } = useCodeAutoSave();
  const { onRunEditorStream } = useRunEditorStream();

  const { codeEditorRef } = useContext(ForwardRefContext);

  const {
    contentRef,
    artifactContentRef,
    selectionBoxRef,
    selectionBox,
    selectionIndexes,
    isSelectionActive,
    isValidSelectionOrigin,
    isInputVisible,
    handleCleanupState,
  } = useCodeEditorSelection();

  const debouncedSave = useDebouncedCallback(async (codeContent: string) => {
    await onCodeAutoSave(codeContent);
  }, DEBOUNCE_DELAY);

  const handleCreateEditor = useCallback(
    (view: EditorView) => {
      codeEditorRef.current = view;
      setView(view);
    },
    [codeEditorRef],
  );

  const handleSubmit = useCallback(
    async (content: string) => {
      const userMessage = getHumanMessage(content);

      const input: EditorGraphInput = {
        userMessage,
        ...(selectionIndexes && {
          highlightedCode: {
            startCharIndex: selectionIndexes.start,
            endCharIndex: selectionIndexes.end,
          },
        }),
        currentCodingLanguage:
          codeEditorData?.language as ProgrammingLanguageOptions,
      };

      await onCodeAutoSave(codeEditorData?.content || '');

      handleCleanupState();

      await onRunEditorStream(input);
    },
    [
      selectionIndexes,
      codeEditorData,
      handleCleanupState,
      onRunEditorStream,
      onCodeAutoSave,
    ],
  );

  const getExtensions = () => {
    const baseExtensions = codeEditorData?.language
      ? [getLanguageExtension(codeEditorData.language)]
      : [];

    return [
      ...baseExtensions,
      history(),
      keymap.of(historyKeymap),
      ...(isWordWrapEnabled ? [EditorView.lineWrapping] : []),
    ];
  };

  const extensions = getExtensions();

  const handleChange = (value: string) => {
    if (view) {
      setCodeEditorData({
        content: value,
        undoStepsCount: undoDepth(view.state),
        redoStepsCount: redoDepth(view.state),
      });

      debouncedSave(value);
    }
  };

  const content = codeEditorData?.content
    ? cleanContent(codeEditorData?.content)
    : '';

  return (
    <div ref={contentRef}>
      <div
        ref={artifactContentRef}
        className={classNames(styles.container, {
          [styles.withOpacity]: isEditorStreaming,
        })}
      >
        <CodeMirror
          editable
          theme={isEditorDarkTheme ? 'dark' : 'light'}
          value={content}
          extensions={extensions}
          onChange={handleChange}
          onCreateEditor={handleCreateEditor}
        />
      </div>

      {selectionBox && isSelectionActive && isValidSelectionOrigin && (
        <CodeFloatingElementPlugin
          ref={selectionBoxRef}
          isInputVisible={isInputVisible}
          selectionBox={selectionBox}
          selectionIndexes={selectionIndexes}
          handleSubmitMessage={handleSubmit}
          handleSelectionBoxMouseDown={(e) => e.stopPropagation()}
          handleCleanupState={handleCleanupState}
        />
      )}
    </div>
  );
};
